import React, { useMemo, useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'
import { FixedSizeList as List } from 'react-window'
import { motion } from 'framer-motion'
import HoverTooltip from '@/components/ui/HoverTooltip'
import './OrderBook.scss'
import useI18n from '@/hooks/useI18n'
import { i18n } from '@/locale/i18n'
import Currency from '@/components/currency/index.js'
import appConstants from '@/constants'
import ButtonIcon from '@/components/ui/ButtonIcon'
import TagContainer from '@/components/ui/Tag-container/TagContainer'
import { AlignVertically } from '@/assets/icons'

const FILL_PREVIEW_COLOR = 'rgb(185 11 245)'

const orderBookScrollOffsets = new Map()

export { orderBookScrollOffsets }

// Play money shows a decimal (0.98). Real money shows cents (98.0¢).
export function formatBookPrice(value, currencyId) {
  if (value == null || value === '') return null
  const price = Number(value)
  if (!Number.isFinite(price)) return null
  return currencyId === 'OOM' ? price.toFixed(2) : `${(price * 100).toFixed(1)}¢`
}

const getFillFractionForPrice = (fillPreviewByPrice, price) => {
  if (!fillPreviewByPrice || price == null) return 0
  if (fillPreviewByPrice[price] != null) return fillPreviewByPrice[price]
  const key = Object.keys(fillPreviewByPrice).find(k => Math.abs(parseFloat(k) - price) < 0.001)
  return key != null ? fillPreviewByPrice[key] : 0
}

const OrderEntry = React.memo(
  ({
    price,
    cumulative_amount,
    cumulative_shares,
    side = 'ask', // 'ask' (purple) or 'bid' (green)
    me,
    isHighlighted = false,
    label = null,
    total_shares,
    total_user_shares = 0,
    total_user_shares_requested = 0,
    maxCumulativeAmount,
    onPriceSelect,
    isSelected = false,
    formatNumber,
    currency,
    onInfoClick = () => {},
    InfoIcon,
    CurrencyComponent,
    adminMode = false,
    fillFraction = 0,
    showFillPreview = false,
  }) => {
    const percentWidth = useMemo(() => {
      const logCumulative = Math.log(cumulative_amount + 1)
      const logMax = Math.log(maxCumulativeAmount + 1)
      return Math.max(Math.min(Math.round((logCumulative / logMax) * 100), 100), 1)
    }, [cumulative_amount, maxCumulativeAmount])

    const fillPreviewWidthPercent = useMemo(() => {
      if (!showFillPreview || fillFraction <= 0) return 0
      return Math.max(Math.min(Math.round(fillFraction * 100), 100), 1)
    }, [showFillPreview, fillFraction])

    const showOverlay = total_user_shares > 0

    const handleClick = React.useCallback(() => {
      onPriceSelect(price)
    }, [onPriceSelect, price])

    const handleInfoClick = React.useCallback(() => {
      onInfoClick(total_user_shares_requested, total_user_shares)
    }, [onInfoClick, total_user_shares_requested, total_user_shares])

    const isAsk = side === 'ask'
    // Asks: #9966FF (purple), Bids: green
    const barColor = isAsk ? 'rgba(153, 102, 255, 0.20)' : 'rgba(34, 197, 94, 0.18)'
    const sideClass = isAsk ? 'order-entry--ask' : 'order-entry--bid'
    const selectedClass = isSelected ? 'order-entry--selected' : ''

    const formattedPrice = formatBookPrice(price, currency?.id)

    return (
      <div
        className={`order-entry ${sideClass} ${selectedClass}`}
        onClick={handleClick}>
        {/* Mobile-only bar — absolute on the row, may extend behind all columns */}
        <div
          className="order-entry__bar order-entry__bar--mobile"
          style={{ width: percentWidth + '%', backgroundColor: barColor }}
        />

        {/* Mobile-only Asks/Bids column (keeps Price column consistent in size) */}
        <div className="order-entry__label-col">
          {label && (
            <TagContainer text={label} type={isAsk ? 'informative2' : 'positive'} />
          )}
        </div>

        {/* Spacer holds the desktop volume bar (% is relative to spacer width, not row) */}
        <div className="order-entry__spacer">
          <div
            className="order-entry__bar order-entry__bar--desktop"
            style={{ width: percentWidth + '%', backgroundColor: barColor }}
          />
          {label && (
            <span className="order-entry__tag-desktop">
              <TagContainer text={label} type={isAsk ? 'informative2' : 'positive'} />
            </span>
          )}
        </div>

        {/* Price */}
        <div className="order-entry__price">
          <span>{formattedPrice}</span>
          {showOverlay && InfoIcon && (
            <HoverTooltip
              offset={4}
              content={`${i18n('bet.filled')}: ${formatNumber(total_user_shares_requested - total_user_shares, 2, 2)}/${formatNumber(total_user_shares_requested, 0, 0)}`}>
              <InfoIcon width="14px" className="block cursor-pointer" onClick={handleInfoClick} />
            </HoverTooltip>
          )}
        </div>

        {/* Shares */}
        <div className="order-entry__shares">
          <Currency value={total_shares} showSuffix={false} noNumberFlow />
        </div>

        {/* Total */}
        <div className={`order-entry__total ${isHighlighted ? 'order-entry__total--highlighted' : ''}`}>
          <CurrencyComponent
            currency={currency?.id === 'OOM' ? 'OOM' : 'USD'}
            value={cumulative_amount}
            showLeftLeadingZero={false}
            noNumberFlow
          />
        </div>

        {adminMode && (
          <div className={`order-entry__total ${isHighlighted ? 'order-entry__total--highlighted' : ''}`}>
            <CurrencyComponent
              currency={currency?.id === 'OOM' ? 'OOM' : 'USD'}
              value={cumulative_shares}
              showLeftLeadingZero={false}
              noNumberFlow
            />
          </div>
        )}
        {showFillPreview && fillPreviewWidthPercent > 0 && (
          <div
            className="absolute left-0 right-0 bottom-0 h-0.5 pointer-events-none z-20"
            aria-hidden
          >
            <div
              className="h-full transition-[width] duration-150"
              style={{
                width: `${fillPreviewWidthPercent}%`,
                backgroundColor: FILL_PREVIEW_COLOR,
              }}
            />
          </div>
        )}
      </div>
    )
  }
)

OrderEntry.displayName = 'OrderEntry'

const SkeletonOrderEntry = React.memo(({ index = 0, total = 4, reverse = false }) => {
  // Calculate progressive width based on index (20% to 80% range) - memoized
  const progressiveWidth = useMemo(() => {
    let width = Math.min(20 + (index * 60) / (total - 1), 80)

    // For buy orders, reverse the width progression (highest at top)
    if (reverse) {
      width = 80 - (index * 60) / (total - 1)
    }

    return width
  }, [index, total, reverse])

  return (
    <div className="flex items-center gap-4 h-6">
      <div className="flex-1 relative h-full">
        <div className="h-full opacity-30 rounded skeleton" style={{ width: `${progressiveWidth}%` }}></div>
      </div>
      <div className="w-16">
        <div className="h-3 rounded w-12 skeleton"></div>
      </div>
      <div className="w-16">
        <div className="h-3 rounded w-10 skeleton"></div>
      </div>
      <div className="w-16">
        <div className="h-3 rounded w-12 skeleton"></div>
      </div>
    </div>
  )
})

SkeletonOrderEntry.displayName = 'SkeletonOrderEntry'

const OrderBookChart = ({
    orders = { buySeries: [], sellSeries: [] },
    isLoading = false,
    me = null,
    spread = 0,
    lastPrice = null,
    selectedPrice = null,
    onPriceSelect = () => {},
    formatNumber = value => value?.toString() || '-',
    currency = { id: 'USD' },
    onInfoClick = () => {},
    InfoIcon = null,
    containerRef = null,
    scrollOffsetRef = null,
    scrollCacheKey = null,
    CurrencyComponent = Currency,
    isVisible = true,
    positionType = appConstants.position.LONG,
    constants = appConstants,
    i18nYes = 'Yes',
    i18nNo = 'No',
    adminMode = false,
    yesnoMarket = false,
    fillPreviewByPrice = null,
    hideOrderBookColHeaders = { labels: false, prices: false, shares: false, amounts: false, cumulativeShares: false },
  }) => {
    // Memoize max cumulative amount calculation to prevent recalculation on every render
    const maxCumulativeAmount = useMemo(() => {
      return Math.max(
        ...(orders.buySeries?.map(o => o.cumulative_amount) || []),
        ...(orders.sellSeries?.map(o => o.cumulative_amount) || []),
        0.0001
      )
    }, [orders.buySeries, orders.sellSeries])

    // Memoize skeleton rendering function
    const renderSkeletonOrders = useMemo(
      () =>
        (count = 3, reverse = false) => {
          return (
            <div className="flex flex-col gap-1">
              {Array.from({ length: count })?.map((_, index) => (
                <SkeletonOrderEntry key={`skeleton-${index}`} index={index} total={count} reverse={reverse} />
              ))}
            </div>
          )
        },
      []
    )

    // Build a single virtualized list of rows: buys, spread, sells
    const rows = useMemo(() => {
      const items = []
      const buy = orders?.buySeries || []
      const sell = orders?.sellSeries || []
      for (let i = 0; i < buy.length; i += 1) {
        items.push({
          type: 'buy',
          order: buy[i],
          isHighlighted: i === buy.length - 1,
          label: i === buy.length - 1 ? 'Asks' : null,
        })
      }
      items.push({ type: 'spread' })
      for (let j = 0; j < sell.length; j += 1) {
        items.push({
          type: 'sell',
          order: sell[j],
          isHighlighted: j === 0,
          label: j === 0 ? 'Bids' : null,
        })
      }
      return items
    }, [orders?.buySeries, orders?.sellSeries])

    const itemSize = 34 // px; 32px row + 2px gap
    const listHeight = 288 // px; matches h-72
    const spreadCenterOffset = useMemo(() => {
      const buyLen = orders?.buySeries?.length || 0
      const spreadRowOffset = buyLen * itemSize
      return Math.max(0, spreadRowOffset - listHeight / 2 + itemSize / 2)
    }, [orders?.buySeries?.length, listHeight, itemSize])

    const internalScrollOffsetRef = useRef(null)
    const persistedScrollRef = scrollOffsetRef ?? internalScrollOffsetRef
    const hadListRowsRef = useRef(false)
    if (rows.length > 1) {
      hadListRowsRef.current = true
    }

    const readPersistedScroll = useCallback(() => {
      if (persistedScrollRef.current != null) return persistedScrollRef.current
      if (scrollCacheKey != null) return orderBookScrollOffsets.get(scrollCacheKey) ?? null
      return null
    }, [persistedScrollRef, scrollCacheKey])

    const writePersistedScroll = useCallback(
      offset => {
        persistedScrollRef.current = offset
        if (scrollCacheKey != null) {
          orderBookScrollOffsets.set(scrollCacheKey, offset)
        }
      },
      [persistedScrollRef, scrollCacheKey]
    )

    const listInitialScrollOffset = readPersistedScroll() ?? spreadCenterOffset

    // Keep a ref to the list
    const listRef = useRef(null)
    const prevIsVisibleRef = useRef(isVisible)
    const hasInitializedRef = useRef(false)
    const currentScrollOffsetRef = useRef(listInitialScrollOffset)

    const restorePersistedScroll = useCallback(() => {
      const saved = readPersistedScroll()
      if (saved == null || !listRef.current) return
      const maxOffset = Math.max(0, rows.length * itemSize - listHeight)
      const clamped = Math.min(saved, maxOffset)
      try {
        listRef.current.scrollTo(clamped)
        currentScrollOffsetRef.current = clamped
      } catch {
        /* noop */
      }
    }, [readPersistedScroll, rows.length, itemSize, listHeight])

    useLayoutEffect(() => {
      restorePersistedScroll()
    }, [selectedPrice, restorePersistedScroll])

    /* Switching tab (or outcome) reuses this instance, so the stored offset would
       carry over. Drop it and queue a re-centre on the spread row. */
    const prevScrollCacheKeyRef = useRef(scrollCacheKey)
    const pendingSpreadCenterRef = useRef(false)

    useLayoutEffect(() => {
      if (prevScrollCacheKeyRef.current === scrollCacheKey) return
      prevScrollCacheKeyRef.current = scrollCacheKey
      persistedScrollRef.current = null
      if (scrollCacheKey != null) {
        orderBookScrollOffsets.delete(scrollCacheKey)
      }
      pendingSpreadCenterRef.current = true
    }, [scrollCacheKey, persistedScrollRef])

    /* Runs again as rows arrive, so a tab switched before its orders load still centres. */
    useLayoutEffect(() => {
      if (!pendingSpreadCenterRef.current || !isVisible || !listRef.current) return
      if (rows.length <= 1) return

      const buyLen = orders?.buySeries?.length || 0
      try {
        listRef.current.scrollToItem(buyLen, 'center')
        writePersistedScroll(spreadCenterOffset)
        currentScrollOffsetRef.current = spreadCenterOffset
        pendingSpreadCenterRef.current = false
      } catch {
        /* noop */
      }
    }, [rows.length, isVisible, orders?.buySeries?.length, spreadCenterOffset, writePersistedScroll])

    // Only scroll when accordion first opens
    useEffect(() => {
      // Early return if not visible - don't do anything
      if (!isVisible) {
        if (prevIsVisibleRef.current !== isVisible) {
          prevIsVisibleRef.current = isVisible
        }
        return
      }

      const justOpened = !prevIsVisibleRef.current && isVisible
      prevIsVisibleRef.current = isVisible

      if (!justOpened || hasInitializedRef.current || !listRef.current) {
        return
      }

      if (readPersistedScroll() != null) {
        hasInitializedRef.current = true
        return
      }

      const buyLen = orders?.buySeries?.length || 0

      const timer = setTimeout(() => {
        if (listRef.current && isVisible) {
          try {
            listRef.current.scrollToItem(buyLen, 'center')
            writePersistedScroll(spreadCenterOffset)
            currentScrollOffsetRef.current = spreadCenterOffset
            hasInitializedRef.current = true
          } catch (error) {
            console.warn('[OrderBookChart] Scroll failed:', error)
          }
        }
      }, 350)

      return () => {
        clearTimeout(timer)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible])

    const t = useI18n()
    const formattedLastPrice = formatBookPrice(lastPrice, currency?.id)
    const lastPriceText = formattedLastPrice == null
      ? null
      : t('order_book.last_price', { price: formattedLastPrice })
    const formattedSpread = spread > 0 ? formatBookPrice(spread, currency?.id) : null
    const spreadText = formattedSpread == null
      ? null
      : t('order_book.spread', { price: formattedSpread })
    const showBothLabels = Boolean(lastPriceText && spreadText)

    // Track scroll position to show "center" button when spread row is off-screen
    const [isSpreadVisible, setIsSpreadVisible] = useState(true)
    const animationFrameRef = useRef(null)
    const spreadRowIndex = orders?.buySeries?.length || 0
    const handleScroll = useCallback(
      ({ scrollOffset }) => {
        writePersistedScroll(scrollOffset)
        currentScrollOffsetRef.current = scrollOffset
        const spreadTop = spreadRowIndex * itemSize
        const spreadBottom = spreadTop + itemSize
        const viewTop = scrollOffset
        const viewBottom = scrollOffset + listHeight
        setIsSpreadVisible(spreadBottom > viewTop && spreadTop < viewBottom)
      },
      [spreadRowIndex, itemSize, listHeight, writePersistedScroll]
    )

    // Animated re-center: tweens scrollOffset toward the spread row over ~400ms
    const recenterToSpread = useCallback(() => {
      if (!listRef.current) return
      const spreadOffset = spreadRowIndex * itemSize
      const target = Math.max(0, spreadOffset - listHeight / 2 + itemSize / 2)
      const start = currentScrollOffsetRef.current
      const distance = target - start
      if (Math.abs(distance) < 1) return

      const duration = 400 // ms
      const startTime = performance.now()
      // easeInOutCubic
      const ease = progress => (
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2
      )

      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)

      const step = now => {
        const elapsed = now - startTime
        const progress = Math.min(1, elapsed / duration)
        const eased = ease(progress)
        const next = start + distance * eased
        try {
          listRef.current.scrollTo(next)
        } catch (e) {
          /* noop */
        }
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(step)
        } else {
          animationFrameRef.current = null
          writePersistedScroll(target)
          currentScrollOffsetRef.current = target
        }
      }
      animationFrameRef.current = requestAnimationFrame(step)
    }, [spreadRowIndex, itemSize, listHeight, writePersistedScroll])

    // Cleanup any in-flight animation on unmount
    useEffect(() => {
      return () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
      }
    }, [])

    return (
      <>
        {/* Column headers */}
        <div className="order-book__col-headers">
          {/* Mobile-only empty cell to mirror the label column inside each row */}
          <div className="order-book__col-headers__label" />
          <div className="flex-1" />
          <div className="order-book__col-headers__price">Price</div>
          <div className="order-book__col-headers__num">Shares</div>
          <div className="order-book__col-headers__num">Total</div>
          {adminMode && !hideOrderBookColHeaders.cumulativeShares && (
            <div className="order-book__col-headers__num">C.Shares</div>
          )}
        </div>

        {/* Chart Container (virtualized) */}
        <motion.div
          id="chart-order-book"
          className="relative"
          transition={{ duration: 0.4, ease: 'easeOut' }}>
          {rows.length > 1 && (
            <div className="order-book__center-btn">
              <HoverTooltip content="Recenter book">
                <ButtonIcon
                  variant="normal"
                  size="small"
                  cursorPointer
                  highlightOnHover
                  isDisabled={isSpreadVisible}
                  handleOnClick={recenterToSpread}
                  ariaLabel="Recenter book">
                  <AlignVertically />
                </ButtonIcon>
              </HoverTooltip>
            </div>
          )}
          {isLoading && (orders?.buySeries?.length || 0) + (orders?.sellSeries?.length || 0) === 0 && !hadListRowsRef.current ? (
            <div className="h-72">
              {renderSkeletonOrders(4, true)}
              <div className="border-t border-b border-solid border-Neutral-3 py-2 my-1" />
              {renderSkeletonOrders(4)}
            </div>
          ) : rows.length <= 1 && !hadListRowsRef.current ? (
            <div className="h-72 flex items-center justify-center text-sm text-Neutral-4">
              {i18n('place_bet.no_orders_available')}
            </div>
          ) : (
            <List
              height={listHeight}
              width={'100%'}
              itemCount={rows.length}
              itemSize={itemSize}
              initialScrollOffset={listInitialScrollOffset}
              ref={listRef}
              outerRef={containerRef}
              onScroll={handleScroll}
              className="will-change-transform scrollbar-hide">
              {({ index, style }) => {
                const row = rows[index]
                if (row.type === 'spread') {
                  return (
                    <div style={style} id="middle-order-book" className="order-book__spread-wrap">
                      <div className="order-book__spread-divider" />
                      <div
                        className={`order-book__spread${
                          showBothLabels ? '' : ' order-book__spread--single'
                        }`}>
                        {lastPriceText ? <span>{lastPriceText}</span> : null}
                        {spreadText ? <span>{spreadText}</span> : null}
                      </div>
                      <div className="order-book__spread-divider" />
                    </div>
                  )
                }
                const order = row.order
                const isBuy = row.type === 'buy' // top section = asks (red)
                const fillFraction =
                  isBuy && adminMode && fillPreviewByPrice
                    ? getFillFractionForPrice(fillPreviewByPrice, order.price)
                    : 0
                return (
                  <div style={style}>
                    <OrderEntry
                      price={order.price}
                      cumulative_amount={order.cumulative_amount}
                      cumulative_shares={order.cumulative_shares}
                      side={isBuy ? 'ask' : 'bid'}
                      label={row.label}
                      total_shares={order.total_shares}
                      total_user_shares={order.total_user_shares}
                      total_user_shares_requested={order.total_user_shares_requested}
                      isHighlighted={row.isHighlighted}
                      maxCumulativeAmount={maxCumulativeAmount}
                      onPriceSelect={onPriceSelect}
                      isSelected={selectedPrice === order.price}
                      formatNumber={formatNumber}
                      currency={currency}
                      onInfoClick={onInfoClick}
                      InfoIcon={InfoIcon}
                      CurrencyComponent={CurrencyComponent}
                      me={me}
                      adminMode={adminMode}
                      fillFraction={fillFraction}
                      showFillPreview={isBuy && adminMode && Boolean(fillPreviewByPrice)}
                    />
                  </div>
                )
              }}
            </List>
          )}
        </motion.div>
      </>
    )
  }

OrderBookChart.displayName = 'OrderBookChart'

// Custom comparison function to debug re-renders
const arePropsEqual = (prevProps, nextProps) => {
  const keysToCompare = [
    'orders',
    'isLoading',
    'spread',
    'lastPrice',
    'selectedPrice',
    'isVisible',
    'positionType',
    'adminMode',
    'yesnoMarket',
    'fillPreviewByPrice',
    'i18nYes',
    'i18nNo',
  ]

  const differences = []
  let hasChanges = false

  keysToCompare.forEach(key => {
    if (key === 'orders') {
      const prevBuyLen = prevProps.orders?.buySeries?.length || 0
      const nextBuyLen = nextProps.orders?.buySeries?.length || 0
      const prevSellLen = prevProps.orders?.sellSeries?.length || 0
      const nextSellLen = nextProps.orders?.sellSeries?.length || 0
      
      if (prevBuyLen !== nextBuyLen || prevSellLen !== nextSellLen) {
        hasChanges = true
        differences.push(`orders: buySeries ${prevBuyLen}→${nextBuyLen}, sellSeries ${prevSellLen}→${nextSellLen}`)
      }
    } else if (prevProps[key] !== nextProps[key]) {
      hasChanges = true
      differences.push(`${key}: ${prevProps[key]} → ${nextProps[key]}`)
    }
  })

  // Check if callbacks are the same reference
  if (prevProps.onPriceSelect !== nextProps.onPriceSelect) {
    hasChanges = true
    differences.push('onPriceSelect: callback reference changed')
  }
  if (prevProps.onInfoClick !== nextProps.onInfoClick) {
    hasChanges = true
    differences.push('onInfoClick: callback reference changed')
  }
  if (prevProps.formatNumber !== nextProps.formatNumber) {
    hasChanges = true
    differences.push('formatNumber: callback reference changed')
  }

  if (hasChanges) {
    return false // Re-render
  }

  return true // Skip re-render
}

export default React.memo(OrderBookChart, arePropsEqual)
